// shared/src/prompts/_grounding.ts
// Pure GROUNDING FACTS block builder, prepended to every LLM user-prompt
// across M-stage + P-stage chains.
//
// Why: prior to 2026-05-23 no prompt was given "TODAY" — analysts invented
// past dates as "future" catalysts (e.g. /amm /brief Zone 3 TIMING showed
// "Fed minutes April 28-29 release" on 2026-05-23). See user thread
// 2026-05-23 in conversation history.
//
// Contract: every chain stage (m.1/m.2/m.3/p.1/p.2/p.4/p.5/p.6) calls
// renderGroundingFacts(...) and prepends the returned block to its user
// message. Skeptics use the same block as the audit baseline for
// cite='factual_error' (forward-field stale dates) + cite='reasonability'
// (out-of-universe attribution).
//
// PURE: no I/O, no env, no Date.now() — all inputs explicit. Callers pass
// today_iso from the chain stage (which reads it from ctx.today_iso
// populated by i.1.fetch). Tests inject deterministic fixtures.

export interface GroundingFacts {
  /** ISO YYYY-MM-DD — "today" anchor for forward-date discipline. */
  readonly today_iso:        string;
  /** ISO datetime — refresh_snapshots.created_at of the payload feeding this brief. */
  readonly snapshot_as_of:   string;
  /** Minutes between snapshot_as_of and today_iso (informational; LLM may note staleness). */
  readonly data_age_min:     number;
  /** Ticker symbols under analysis in this brief — drives in-universe attribution check. */
  readonly ticker_universe:  ReadonlyArray<string>;
  /** Per-ticker only: the symbol being analyzed in THIS call. */
  readonly ticker?:          string;
  /** Per-ticker only: current price from ticker_data — drives numeric grounding check. */
  readonly current_price?:   number | null;
  /** Per-ticker only: ISO datetime of the price tick (usually == snapshot_as_of). */
  readonly price_as_of?:     string | null;
}

/**
 * Builds the GROUNDING FACTS block prepended to user prompts. Output is a
 * fixed-format text block ending with a trailing newline so callers can
 * concatenate without managing spacing.
 *
 * Per-ticker fields (ticker / current_price / price_as_of) are omitted from
 * the rendered block when undefined — M-stage prompts get the universe-level
 * block only; P-stage prompts get universe + per-ticker.
 */
export function renderGroundingFacts(g: GroundingFacts): string {
  const lines: string[] = [
    'GROUNDING FACTS (authoritative — use for forward-date discipline, numeric grounding, in-universe attribution):',
    `  TODAY:           ${g.today_iso}`,
    `  SNAPSHOT_AS_OF:  ${g.snapshot_as_of}`,
    `  DATA_AGE_MIN:    ${g.data_age_min}`,
    `  TICKER_UNIVERSE: ${g.ticker_universe.length === 0 ? '(empty)' : g.ticker_universe.join(', ')}`,
  ];
  if (g.ticker !== undefined) {
    lines.push(`  TICKER:          ${g.ticker}`);
  }
  if (g.current_price !== undefined && g.current_price !== null) {
    lines.push(`  CURRENT_PRICE:   ${g.current_price}`);
  } else if (g.current_price === null) {
    lines.push(`  CURRENT_PRICE:   (unavailable — payload missing live price)`);
  }
  if (g.price_as_of !== undefined && g.price_as_of !== null) {
    lines.push(`  PRICE_AS_OF:     ${g.price_as_of}`);
  }
  return lines.join('\n') + '\n';
}

/**
 * Compute data_age_min from a refresh_snapshots.created_at ISO datetime and
 * a reference "now" (defaults to Date.now() — callers in chain stages should
 * inject ctx.today_iso-derived reference for determinism, but workers running
 * i.1.fetch can use the default since the value is informational, not used
 * for any control flow).
 */
export function computeDataAgeMin(snapshot_as_of_iso: string, now_ms = Date.now()): number {
  const snapMs = Date.parse(snapshot_as_of_iso);
  if (!Number.isFinite(snapMs)) {
    throw new Error(`[grounding] computeDataAgeMin: bad snapshot_as_of "${snapshot_as_of_iso}"`);
  }
  const ageMin = Math.round((now_ms - snapMs) / 60_000);
  return ageMin;
}

// ─── V2 (chain-redesign-v2) ────────────────────────────────────────────────
// Per design-notes L1: GROUNDING FACTS = world-state facts only. No
// ticker_universe, no output-shape constraints. Output shape lives in
// OutputSchema + code validators. Universe enforcement is a translator
// concern, not a prompt concern.

export interface WorldStateGrounding {
  readonly today_iso:      string;
  readonly snapshot_as_of: string;
  readonly data_age_min:   number;
}

export function renderWorldStateGrounding(g: WorldStateGrounding): string {
  return [
    'GROUNDING FACTS (world-state only — authoritative anchor for date discipline):',
    `  TODAY:           ${g.today_iso}`,
    `  SNAPSHOT_AS_OF:  ${g.snapshot_as_of}`,
    `  DATA_AGE_MIN:    ${g.data_age_min}`,
  ].join('\n') + '\n';
}

export function pickWorldStateGrounding(src: {
  readonly today_iso?:      string;
  readonly snapshot_as_of?: string;
  readonly data_age_min?:   number;
}): WorldStateGrounding | undefined {
  if (src.today_iso === undefined || src.snapshot_as_of === undefined || src.data_age_min === undefined) {
    return undefined;
  }
  return {
    today_iso:      src.today_iso,
    snapshot_as_of: src.snapshot_as_of,
    data_age_min:   src.data_age_min,
  };
}

/**
 * Compact selector that lifts grounding fields off a ChainCtx-like or
 * per-ticker-input-like object into a GroundingFacts record. Returns
 * undefined when any of the 4 base fields are missing — preserves the
 * "no GROUNDING block in prompts when chain didn't populate" path for
 * legacy fixture tests.
 *
 * For per-ticker calls, pass the `perTicker` overlay to attach
 * ticker / current_price / price_as_of without re-spelling base fields.
 */
export function pickGrounding(
  src: {
    readonly today_iso?:       string;
    readonly snapshot_as_of?:  string;
    readonly data_age_min?:    number;
    readonly ticker_universe?: ReadonlyArray<string>;
  },
  perTicker?: { readonly ticker: string; readonly current_price: number | null },
): GroundingFacts | undefined {
  if (
    src.today_iso       === undefined ||
    src.snapshot_as_of  === undefined ||
    src.data_age_min    === undefined ||
    src.ticker_universe === undefined
  ) {
    return undefined;
  }
  const base: GroundingFacts = {
    today_iso:        src.today_iso,
    snapshot_as_of:   src.snapshot_as_of,
    data_age_min:     src.data_age_min,
    ticker_universe:  src.ticker_universe,
  };
  if (perTicker) {
    return {
      ...base,
      ticker:        perTicker.ticker,
      current_price: perTicker.current_price,
      price_as_of:   src.snapshot_as_of,
    };
  }
  return base;
}
